import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import { keys } from '../../api/keys'
import type { AsyncState } from '../../platform/async/Async'
import { fromQuery } from '../../platform/async/fromQuery'
import type { components } from '../../api/generated/schema'
import { narrowRole, type Role } from './roles'
import { HouseholdError } from './householdError'

/** Mirrors the backend's `InvitationStatus` enum (`api/v1/households/*`). An
 *  invitation outside this set has never been observed on the wire — the
 *  column is a native Postgres enum, same as `household_role` (`roles.ts`) —
 *  but `status` is typed `string | null`, not narrowed to this union, so a
 *  row the client doesn't recognise still renders rather than vanishing. */
export type InvitationStatus = 'pending' | 'accepted' | 'revoked' | 'expired'

function narrowStatus(value: string): InvitationStatus | null {
  return value === 'pending' || value === 'accepted' || value === 'revoked' || value === 'expired'
    ? value
    : null
}

/**
 * An invitation in the shape the screen needs, not the wire's.
 *
 * `role` and `rawRole` both survive the trip, for the same reason
 * `Member` keeps both (`useMembers.ts`): `InvitationResponse` also types
 * `role` as a bare `string`, so a value outside the enum is possible and the
 * row still has to render it. `status` mirrors that same pattern — narrowed
 * to `InvitationStatus | null`, `null` for anything unrecognised, and the
 * row is expected to fall back to showing nothing special rather than
 * inventing a state.
 *
 * `token` is mapped through as `null` from this endpoint, always. The wire
 * type has the field, and the schema's own description says it is populated
 * only on creation — so the pending list cannot offer a copy-link, and the
 * type here says so rather than leaving a later task to discover it.
 */
export type Invitation = {
  id: string
  email: string
  role: Role | null
  rawRole: string
  status: InvitationStatus | null
  createdAt: string
  expiresAt: string
  token: string | null
}

/**
 * The wire row → `Invitation` transform, on its own so `src/routes/probe/Probe.tsx`'s
 * fixture can call the SAME function `listInvitations` does rather than
 * repeating it — `useMembers.ts`'s own `toMember` is the precedent (issue
 * #329, architect finding N1). `household_name`, `token` and `code` are on
 * the wire type but never make it into `Invitation`: `household_name` this
 * screen already knows from `useHousehold`, and `token`/`code` are populated
 * only on creation (the schema's own description), never on this list
 * endpoint, so `Invitation.token` is hard-coded `null` rather than trusted
 * through.
 */
export function toInvitation(i: components['schemas']['InvitationResponse']): Invitation {
  return {
    id: i.id,
    email: i.email,
    role: narrowRole(i.role),
    rawRole: i.role,
    status: narrowStatus(i.status),
    createdAt: i.created_at,
    expiresAt: i.expires_at,
    token: null,
  }
}

async function listInvitations(householdId: string): Promise<Invitation[]> {
  const { data, error, response } = await api.GET(
    '/api/v1/households/{household_id}/invitations',
    { params: { path: { household_id: householdId } } },
  )
  if (error !== undefined || data === undefined) throw new HouseholdError(error, response.status)
  return data.invitations
    .map(toInvitation)
    .filter((i) => i.status !== 'accepted' && i.status !== 'revoked')
}

/**
 * Pending and expired invitations — not accepted or revoked ones.
 *
 * `GET /invitations` takes an optional `status` filter and this deliberately
 * does not send it. M12-R9: expired invitations stay visible with a resend
 * path, "so the user understands why nothing happened" — filtering to pending
 * would make an expired invitation vanish, which reads as never having been
 * sent.
 *
 * **`accepted`/`revoked` are filtered out client-side, in `listInvitations`,
 * not by the server.** This looks like it contradicts `useMembers`'
 * no-client-filtering doctrine, but that doctrine's own stated reason is
 * "the server does it" — here it cannot. `households.py:449` computes
 * `include_expired = status_filter != PENDING`; sending `status=pending`
 * would flip that to `false` and drop expired rows too, which M12-R9
 * forbids. The single `status` enum param cannot express "pending or
 * expired" in one request, so requesting everything and filtering the two
 * terminal states out here is the only way to get both requirements at
 * once. An unrecognised status (narrows to `null`) is left VISIBLE — failing
 * open on display, since hiding a row this client cannot classify is how an
 * invitation would silently disappear from the panel that exists to show it.
 *
 * **The rule this filter is actually applying: trust the field where
 * something writes it.** `accepted` and `revoked` are written synchronously
 * by the backend the moment they happen (`invitation_service.py:297` and
 * `:460`), so filtering on `status` for those two is filtering on a value
 * that is always current. `expired` is written by nothing — see `isExpired`
 * below — which is exactly why this list keeps expired rows visible instead
 * of also trying to filter them by `status`.
 *
 * `live`, not `synced` (`surfaces.ts`) — `InvitationListResponse` carries no
 * `last_synced_at`, matching `household-members`. Unlike members, `isEmpty`
 * IS passed here: a household with nobody invited is the normal state
 * (`household-invitations`'s own docblock), where the member list can never
 * be empty for the person reading it.
 */
export function useInvitations(householdId: string | undefined): AsyncState<Invitation[]> {
  const query = useQuery({
    queryKey: keys.household.invitations(householdId ?? ''),
    queryFn: () => listInvitations(householdId as string),
    enabled: householdId !== undefined,
  })
  return fromQuery(query, {
    message: () => 'Invitations could not be loaded.',
    recovery: { label: 'Try again', onAct: () => void query.refetch() },
    isEmpty: (data) => data.length === 0,
  })
}

/**
 * Whether `invitation` is expired now, derived from `expiresAt` rather than
 * trusted from `status`.
 *
 * `cleanup_expired_invitations` (backend `invitation_service.py:577`) is
 * defined once and called from nowhere — no scheduler, no task, no endpoint
 * — so `status` never actually transitions to `'expired'` in production; a
 * past-`expiresAt` row arrives as `'pending'` forever. Comparing `expiresAt`
 * to the clock is what makes an expired row reachable at all.
 *
 * Exported, not local to `InvitationRow`, because Task 9's `inviteBlockedReason`
 * needs the same derivation — an invitation whose `expiresAt` has passed must
 * not count as "already pending" even though its `status` still says so — and
 * a second implementation would drift from this one.
 */
export function isExpired(
  invitation: Pick<Invitation, 'status' | 'expiresAt'>,
  now: Date = new Date(),
): boolean {
  return invitation.status === 'expired' || Date.parse(invitation.expiresAt) <= now.getTime()
}

/**
 * Whole days between `iso` and `now` (default: the real clock), floored at
 * zero.
 *
 * The only place invitation age is computed — Task 12 sends it to analytics
 * as `days_since_sent`, and a second implementation would drift from this
 * one. A negative value from clock skew would be a nonsense measurement, so
 * it floors rather than reporting one.
 */
export function daysSince(iso: string, now: Date = new Date()): number {
  const ms = now.getTime() - new Date(iso).getTime()
  return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)))
}

/**
 * `POST /households/{id}/invitations` — Task 9, the write the module exists
 * for.
 *
 * **Returns a `{ create, isPending }` pair rather than the raw mutation**, so
 * the dialog never reaches for `mutateAsync`'s variables object or for the
 * eight other fields TanStack hangs off a mutation. The interface is the two
 * things a form needs: send this, and are we waiting.
 *
 * **`token` survives this one response and no other.** `toInvitation` hard-codes
 * `null` because the LIST endpoint really does send `null` — the schema's own
 * description says token and code are "only returned on creation" — so the
 * mapped row is corrected here, at the single call site where the field is
 * populated. Without it the success panel would have no link to hand over, and
 * issue 305 means the emailed link is dead in a browser: the copy-link IS the
 * delivery mechanism, not a convenience.
 *
 * `code` is deliberately left on the wire. It is a short code for manual entry
 * in the mobile app, and this client has no screen that accepts one — surfacing
 * it would offer the user a value with nowhere to type it.
 *
 * **The address is lowercased on the way out, matching iOS**
 * (`HouseholdUseCases.swift`). The server's own pending check compares the raw
 * string, so two casings of one address create two rows; folding here is what
 * makes `inviteBlockedReason`'s fold — which runs against the list this client
 * already holds — describe the same set the server will.
 *
 * Invalidates the invitations key on success rather than writing the new row
 * into the cache: the server decides `expires_at`, `status` and the id, and a
 * hand-built optimistic row would be a second opinion about all three.
 */
export function useCreateInvitation(householdId: string): {
  create: (email: string, role: Role) => Promise<Invitation>
  isPending: boolean
} {
  const client = useQueryClient()
  const mutation = useMutation({
    mutationFn: async ({ email, role }: { email: string; role: Role }) => {
      const { data, error, response } = await api.POST(
        '/api/v1/households/{household_id}/invitations',
        {
          params: { path: { household_id: householdId } },
          body: { email: email.trim().toLowerCase(), role },
        },
      )
      if (error !== undefined || data === undefined) {
        throw new HouseholdError(error, response.status)
      }
      return { ...toInvitation(data), token: data.token ?? null }
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: keys.household.invitations(householdId) })
    },
  })

  return {
    create: (email, role) => mutation.mutateAsync({ email, role }),
    isPending: mutation.isPending,
  }
}

/**
 * Resend — M12-R8, and the reason the row needs the response rather than a
 * bare success.
 *
 * The server rotates `token` and `code` ON THE SAME ROW and pushes out
 * `expires_at` (`invitation_service.py:535`); it does not insert. So this
 * invalidates and returns, and deliberately writes nothing into the cache
 * itself: an optimistic append would put the same person on screen twice, and
 * an optimistic patch would have to invent an expiry the server owns.
 *
 * The returned `token` is the point. Rotation KILLS the previous link, and per
 * #305 that link is the only way an invitation can be accepted from a browser
 * — so a resend that did not hand back the new one would leave the inviter
 * holding a URL that can no longer be accepted, with nothing on screen saying
 * so. This is also the second and last moment a token is readable: the list
 * endpoint does not return one.
 */
export function useResendInvitation(householdId: string): {
  resend: (id: string) => Promise<Invitation>
  isPending: boolean
} {
  const client = useQueryClient()
  const mutation = useMutation({
    mutationFn: async (id: string) => {
      const { data, error, response } = await api.POST('/api/v1/invitations/{invitation_id}/resend', {
        params: { path: { invitation_id: id } },
      })
      if (error !== undefined || data === undefined) {
        throw new HouseholdError(error, response.status)
      }
      return { ...toInvitation(data), token: data.token ?? null }
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: keys.household.invitations(householdId) })
    },
  })

  return { resend: (id) => mutation.mutateAsync(id), isPending: mutation.isPending }
}

/**
 * Revoke. A `DELETE` that returns no body, so this resolves to `void` — a
 * mutation typed to return the deleted thing would be inventing it.
 *
 * The refetch is the whole update, and a revoked row then LEAVES the list —
 * intentionally. This panel is M12-R7's "pending invitations list", and
 * `toInvitations` drops `accepted` and `revoked` for that reason.
 *
 * The server does not settle it. The client sends no `status` param, so
 * `households.py:449` computes `include_expired = (None != PENDING)` — true —
 * and `list_household_invitations` returns every status including revoked.
 * The filter at the top of this file is the only thing removing it, which
 * makes it load-bearing rather than defensive.
 *
 * Two earlier versions of this comment got this wrong in opposite directions:
 * one cited M12-R9 as keeping revoked invitations visible (M12-R9 is about
 * EXPIRED ones — `PRD-M12-household.md:45`), and one claimed the server
 * filtered them (it does, but only when the client asks for `status=pending`,
 * which it never does). Both were plausible from a partial read of one file.
 * Whoever changes this next should re-read `households.py:449` and the client
 * filter together, because neither says the whole thing alone.
 */
export function useRevokeInvitation(householdId: string): {
  revoke: (id: string) => Promise<void>
  isPending: boolean
} {
  const client = useQueryClient()
  const mutation = useMutation({
    mutationFn: async (id: string) => {
      const { error, response } = await api.DELETE('/api/v1/invitations/{invitation_id}', {
        params: { path: { invitation_id: id } },
      })
      if (error !== undefined) throw new HouseholdError(error, response.status)
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: keys.household.invitations(householdId) })
    },
  })

  return { revoke: (id) => mutation.mutateAsync(id), isPending: mutation.isPending }
}
